import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DiscoverSearch from './DiscoverSearch';
import { useI18n } from '../../i18n/I18nContext';
import logo from '../../style/imges/hopalogo.png';
import '../../style/css/mkthome.css';

/* HOPpA — marketing HOME (/welcome). Hebrew is DEFAULT + fallback; English is an added option.
 * i18n reuses the existing useI18n() context; strings live in translations.js under `home.*`.
 * Ticketmaster-style: image-led MOSAIC hero + sectioned rows of image cards,
 * SHARP small corners, dense-but-scannable. Palette: pink + gray + white only.
 * Everything scoped under `.mh`. */

const IMG = '/img/marketing';

// Nav — labels resolved from translation keys at render time.
const NAV = [
  { href: '#restaurants', key: 'home.nav.restaurants' },
  { href: '#how', key: 'home.nav.how' },
  { href: '#pricing', key: 'home.nav.pricing' },
  { href: '#faq', key: 'home.nav.faq' },
];

// Filter pills — stable id (for the active-state comparison) + translation key.
// `match` = a substring tested (locale-independent) against a sample restaurant's cat/area, so
// selecting a pill actually FILTERS the grid (not a dead button). 'all' shows everything.
const PILLS = [
  { id: 'all', key: 'home.pill.all', match: null },
  { id: 'chef', key: 'home.pill.chef', match: 'שף' },
  { id: 'grill', key: 'home.pill.grill', match: 'בשר' },
  { id: 'fish', key: 'home.pill.fish', match: 'דגים' },
  { id: 'italian', key: 'home.pill.italian', match: 'איטלק' },
  { id: 'asian', key: 'home.pill.asian', match: 'אסייתי' },
  { id: 'mediterranean', key: 'home.pill.mediterranean', match: 'ים תיכוני' },
  { id: 'vegan', key: 'home.pill.vegan', match: 'טבעוני' },
  { id: 'cafe', key: 'home.pill.cafe', match: 'קפה' },
  { id: 'tlv', key: 'home.pill.tlv', match: 'תל אביב' },
  { id: 'jlm', key: 'home.pill.jlm', match: 'ירושלים' },
  { id: 'haifa', key: 'home.pill.haifa', match: 'חיפה' },
];

// The hero mosaic: 1 big featured + smaller tiles. name/cat/area are placeholder sample data.
const MOSAIC = [
  { slug: 'the-brasserie', name: 'הבראסרי', cat: 'צרפתי · ביסטרו', area: 'תל אביב', img: 'interior-lux', size: 'big', today: true },
  { slug: 'ember-grill', name: 'אמבר גריל', cat: 'בשרים · על האש', area: 'ירושלים', img: 'chef', size: 'sm', today: true },
  { slug: 'sushi-koda', name: 'סושי קודה', cat: 'יפני · אסייתי', area: 'תל אביב', img: 'plate-fish', size: 'sm', today: true },
  { slug: 'green-fork', name: 'גרין פורק', cat: 'טבעוני · צמחוני', area: 'תל אביב', img: 'terrace', size: 'sm', today: true },
  { slug: 'la-mar', name: 'לה מאר', cat: 'דגים · פירות ים', area: 'הרצליה', img: 'candlelit', size: 'sm', today: true },
];

// "מסעדות מובחרות" grid — the 6 required sample restaurants (placeholder sample data).
const RESTAURANTS = [
  { slug: 'the-brasserie', name: 'הבראסרי', cat: 'צרפתי · ביסטרו', area: 'תל אביב', img: 'interior-lux' },
  { slug: 'sushi-koda', name: 'סושי קודה', cat: 'יפני · אסייתי', area: 'תל אביב', img: 'plate-fish' },
  { slug: 'ember-grill', name: 'אמבר גריל', cat: 'בשרים · על האש', area: 'ירושלים', img: 'chef' },
  { slug: 'green-fork', name: 'גרין פורק', cat: 'טבעוני · צמחוני', area: 'תל אביב', img: 'terrace' },
  { slug: 'mezze-bar', name: 'מזה בר', cat: 'ים תיכוני · מזרחי', area: 'חיפה', img: 'oasis' },
  { slug: 'la-mar', name: 'לה מאר', cat: 'דגים · פירות ים', area: 'הרצליה', img: 'candlelit' },
];

// Steps + plans + FAQ carry only translation keys; copy lives in translations.js.
const STEPS = [
  { n: '1', title: 'home.step1_title', text: 'home.step1_text' },
  { n: '2', title: 'home.step2_title', text: 'home.step2_text' },
  { n: '3', title: 'home.step3_title', text: 'home.step3_text' },
];

const PLANS = [
  {
    id: 'basic', price: '99', feat: false,
    name: 'home.plan_basic_name', sub: 'home.plan_basic_sub',
    perks: ['home.plan_basic_p1', 'home.plan_basic_p2', 'home.plan_basic_p3', 'home.plan_basic_p4'],
  },
  {
    id: 'pro', price: '199', feat: true,
    name: 'home.plan_pro_name', sub: 'home.plan_pro_sub',
    perks: ['home.plan_pro_p1', 'home.plan_pro_p2', 'home.plan_pro_p3', 'home.plan_pro_p4', 'home.plan_pro_p5'],
  },
  {
    id: 'chains', price: '699', feat: false,
    name: 'home.plan_chains_name', sub: 'home.plan_chains_sub',
    perks: ['home.plan_chains_p1', 'home.plan_chains_p2', 'home.plan_chains_p3', 'home.plan_chains_p4'],
  },
];

const FAQ = [
  { q: 'home.faq_q1', a: 'home.faq_a1' },
  { q: 'home.faq_q2', a: 'home.faq_a2' },
  { q: 'home.faq_q3', a: 'home.faq_a3' },
  { q: 'home.faq_q4', a: 'home.faq_a4' },
  { q: 'home.faq_q5', a: 'home.faq_a5' },
];

function Tile({ r, t }) {
  const cls = r.size === 'big' ? 'mh-tile mh-tile--big' : 'mh-tile';
  return (
    <div className={cls}>
      <Link to={`/b/${r.slug}`} className="mh-card" aria-label={t('home.book_at', { name: r.name })}>
        <img src={`${IMG}/${r.img}.jpg`} alt={r.name} loading={r.size === 'big' ? 'eager' : 'lazy'} />
        {r.today && <span className="mh-badge">{t('home.badge_today')}</span>}
        <div className="mh-card-body">
          <span className="mh-chip">{r.cat}</span>
          <h3 className={`mh-card-name${r.size === 'big' ? '' : ' mh-card-name-sm'}`}>{r.name}</h3>
          <span className="mh-card-meta">{r.area}</span>
        </div>
      </Link>
    </div>
  );
}

export default function MarketingHome() {
  const { t, dir, setLocale, locale } = useI18n();
  const [pill, setPill] = useState('all');
  const [mobileSearch, setMobileSearch] = useState(false);

  // The selected filter pill actually filters the featured grid (by cat/area substring); 'all'
  // (match=null) shows everything. Sample/placeholder data, so some pills legitimately match none.
  const visibleRestaurants = useMemo(() => {
    const m = (PILLS.find((p) => p.id === pill) || {}).match;
    return m ? RESTAURANTS.filter((r) => (r.cat || '').includes(m) || (r.area || '').includes(m)) : RESTAURANTS;
  }, [pill]);

  return (
    <div className="mh" dir={dir}>
      {/* ── 1 · HEADER ─────────────────────────────────────────── */}
      <header className="mh-header">
        <div className="mh-header-in">
          <Link to="/welcome" aria-label={t('home.brand_aria')}><img src={logo} alt="HOPpA" className="mh-logo" /></Link>

          <nav className="mh-nav">
            {NAV.map((n) => <a key={n.href} href={n.href}>{t(n.key)}</a>)}
          </nav>

          <div className="mh-search">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input type="search" placeholder={t('home.search_placeholder')} aria-label={t('home.search_aria')} />
          </div>

          <div className="mh-header-actions">
            <div className="mh-lang" role="group" aria-label={t('nav.langLabel')}>
              <button
                type="button"
                className={`mh-lang-btn${locale === 'he' ? ' is-active' : ''}`}
                aria-pressed={locale === 'he'}
                onClick={() => setLocale('he')}
              >עב</button>
              <button
                type="button"
                className={`mh-lang-btn${locale === 'en' ? ' is-active' : ''}`}
                aria-pressed={locale === 'en'}
                onClick={() => setLocale('en')}
              >EN</button>
            </div>
            <Link to="/login" className="mh-btn mh-btn--text">{t('home.login')}</Link>
            <Link to="/login" className="mh-btn mh-btn--pink">{t('home.register')}</Link>
          </div>

          {/* mobile */}
          <button className="mh-search-icon" aria-label={t('home.search_aria')} onClick={() => setMobileSearch((v) => !v)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          </button>
          <Link to="/login" className="mh-burger" aria-label={t('home.menu_aria')}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
          </Link>
        </div>
        <div className={`mh-mobile-search${mobileSearch ? '' : ' hidden'}`}>
          <div className="mh-search" style={{ display: 'flex' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input type="search" placeholder={t('home.search_placeholder')} aria-label={t('home.search_aria')} />
          </div>
        </div>
      </header>

      {/* ── 2 · HERO MOSAIC ────────────────────────────────────── */}
      <section className="mh-hero">
        <div className="mh-wrap">
          <div className="mh-hero-head">
            <h1>{t('home.hero_title')}</h1>
            <p>{t('home.hero_sub')}</p>
          </div>

          {/* Rich reservation search — sample data page, so no city list; still routes to /discover */}
          <div className="mh-discover">
            <DiscoverSearch cities={[]} initial={{}} />
          </div>

          <div className="mh-mosaic">
            {MOSAIC.map((r) => <Tile key={r.slug + r.img} r={r} t={t} />)}

            {/* מומלץ — slim promo column */}
            <div className="mh-tile mh-tile--promo">
              <div className="mh-promo">
                <span className="mh-promo-tag">{t('home.promo_tag')}</span>
                <h3>{t('home.promo_title')}</h3>
                <p>{t('home.promo_text')}</p>
                <Link to="/discover" className="mh-btn">{t('home.promo_cta')}</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3 · FILTER PILLS ───────────────────────────────────── */}
      <div className="mh-wrap">
        <div className="mh-pills" role="group" aria-label={t('home.filter_aria')}>
          {PILLS.map((p) => (
            <button key={p.id} className="mh-pill" aria-pressed={pill === p.id} onClick={() => setPill(p.id)}>{t(p.key)}</button>
          ))}
        </div>
      </div>

      {/* ── 4 · מסעדות מובחרות ─────────────────────────────────── */}
      <section className="mh-section" id="restaurants">
        <div className="mh-wrap">
          <div className="mh-section-head">
            <div>
              <h2>{t('home.rest_title')}</h2>
              <p>{t('home.rest_sub')}</p>
            </div>
            <Link to="/discover" className="mh-btn mh-btn--ghost">{t('home.rest_all')}</Link>
          </div>

          <div className="mh-grid">
            {visibleRestaurants.map((r) => (
              <Link key={r.slug} to={`/b/${r.slug}`} className="mh-rest">
                <div className="mh-rest-media">
                  <img src={`${IMG}/${r.img}.jpg`} alt={r.name} loading="lazy" />
                  <span className="mh-badge">{t('home.badge_today')}</span>
                </div>
                <div className="mh-rest-body">
                  <span className="mh-rest-name">{r.name}</span>
                  <span className="mh-rest-meta">{r.cat} · {r.area}</span>
                  <span className="mh-rest-cta">{t('home.rest_cta')}</span>
                </div>
              </Link>
            ))}
          </div>
          {visibleRestaurants.length === 0 && (
            <p className="mh-rest-empty">
              {t('home.rest_none')} <Link to="/discover" className="mh-rest-empty-link">{t('home.rest_all')}</Link>
            </p>
          )}
        </div>
      </section>

      {/* ── 5 · B2B OWNER BAND ─────────────────────────────────── */}
      <section className="mh-b2b" id="how">
        <div className="mh-b2b-in">
          <div className="mh-wrap">
            <h2>{t('home.b2b_title')}</h2>
            <p>{t('home.b2b_sub')}</p>
            <div className="mh-steps">
              {STEPS.map((s) => (
                <div className="mh-step" key={s.n}>
                  <span className="mh-step-n">{s.n}</span>
                  <h3>{t(s.title)}</h3>
                  <p>{t(s.text)}</p>
                </div>
              ))}
            </div>
            <Link to="/login" className="mh-btn mh-btn--pink mh-btn--lg">{t('home.b2b_cta')}</Link>
          </div>
        </div>
      </section>

      {/* ── 6 · מחירים ─────────────────────────────────────────── */}
      <section className="mh-section" id="pricing">
        <div className="mh-wrap">
          <div className="mh-section-head">
            <div>
              <h2>{t('home.pricing_title')}</h2>
              <p>{t('home.pricing_sub')}</p>
            </div>
          </div>
          <div className="mh-prices">
            {PLANS.map((p) => (
              <div key={p.id} className={`mh-price${p.feat ? ' mh-price--feat' : ''}`}>
                {p.feat && <span className="mh-price-flag">{t('home.price_flag')}</span>}
                <span className="mh-price-name">{t(p.name)}</span>
                <div className="mh-price-amt">₪{p.price}<small>{t('home.price_per_month')}</small></div>
                <span className="mh-price-sub">{t(p.sub)}</span>
                <ul className="mh-price-list">
                  {p.perks.map((x) => <li key={x}>{t(x)}</li>)}
                </ul>
                <Link to="/login" className={`mh-btn ${p.feat ? 'mh-btn--pink' : 'mh-btn--ghost'}`}>{t('home.price_cta')}</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7 · שאלות נפוצות ───────────────────────────────────── */}
      <section className="mh-section" id="faq">
        <div className="mh-wrap">
          <div className="mh-section-head">
            <div><h2>{t('home.faq_title')}</h2></div>
          </div>
          <div className="mh-faq">
            {FAQ.map((f, i) => (
              <details key={f.q} open={i === 0}>
                <summary>{t(f.q)}</summary>
                <p>{t(f.a)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8 · FOOTER ─────────────────────────────────────────── */}
      <footer className="mh-footer">
        <div className="mh-footer-in">
          <div className="mh-footer-brand">
            <img src={logo} alt="HOPpA" />
            <p>{t('home.footer_tagline')}</p>
          </div>
          <div className="mh-footer-col">
            <h4>{t('home.footer_diners')}</h4>
            <Link to="/discover">{t('home.footer_discover')}</Link>
            <a href="#restaurants">{t('home.footer_featured')}</a>
            <a href="#faq">{t('home.footer_faq')}</a>
          </div>
          <div className="mh-footer-col">
            <h4>{t('home.footer_owners')}</h4>
            <a href="#how">{t('home.footer_how')}</a>
            <a href="#pricing">{t('home.nav.pricing')}</a>
            <Link to="/login">{t('home.footer_login_system')}</Link>
          </div>
          <div className="mh-footer-col">
            <h4>HOPpA</h4>
            <Link to="/login">{t('home.footer_login')}</Link>
            <Link to="/login">{t('home.footer_register')}</Link>
            <a href="#faq">{t('home.footer_contact')}</a>
          </div>
        </div>
        <div className="mh-footer-bar">{t('home.footer_rights', { year: new Date().getFullYear() })}</div>
      </footer>
    </div>
  );
}
